/**
 * AI Key Management Types
 *
 * Types for the AI provider key management UI.
 * Backend stores keys in GCP Secret Manager,
 * Firestore stores metadata only.
 *
 * @module ai-settings/types
 */

/** Supported AI provider types */
export type AIProviderType = 'anthropic' | 'openai' | 'google' | 'apify' | 'perplexity' | 'brightdata';

/** Provider display info */
export interface AIProviderInfo {
  type: AIProviderType;
  name: string;
  description: string;
  keyPrefix: string;
  keyPlaceholder: string;
}

/** Key metadata returned by GET /api/ai/keys */
export interface AIKeyMetadata {
  configured: boolean;
  lastValidated?: string;
  maskedHint?: string;
}

/** Provider status map returned by backend */
export type AIProvidersStatus = Record<AIProviderType, AIKeyMetadata>;

/** Default empty status */
export const DEFAULT_PROVIDERS_STATUS: AIProvidersStatus = {
  anthropic: { configured: false },
  openai: { configured: false },
  google: { configured: false },
  apify: { configured: false },
  perplexity: { configured: false },
  brightdata: { configured: false },
};

/** Provider display configuration */
export const AI_PROVIDERS: AIProviderInfo[] = [
  {
    type: 'anthropic',
    name: 'Anthropic Claude',
    description: 'Claude Sonnet, Opus, Haiku models',
    keyPrefix: 'sk-ant-',
    keyPlaceholder: 'sk-ant-api03-...',
  },
  {
    type: 'openai',
    name: 'OpenAI',
    description: 'GPT-5.5, GPT-5.4, GPT-4o, o-series, and GPT Image models',
    keyPrefix: 'sk-',
    keyPlaceholder: 'sk-proj-...',
  },
  {
    type: 'google',
    name: 'Google Gemini',
    description: 'Gemini 3 Flash Preview, Gemini 3.1 Flash Lite/Pro, and Gemini image models',
    keyPrefix: 'AI',
    keyPlaceholder: 'AIza...',
  },
  {
    type: 'apify',
    name: 'Apify',
    description: 'Creator research scrapers (Instagram, TikTok, LinkedIn, YouTube)',
    keyPrefix: 'apify_api_',
    keyPlaceholder: 'apify_api_...',
  },
  {
    type: 'perplexity',
    name: 'Perplexity',
    description: 'Sonar models for web-grounded research (1-pass structured output)',
    keyPrefix: 'pplx-',
    keyPlaceholder: 'pplx-...',
  },
  {
    type: 'brightdata',
    name: 'Bright Data',
    description: 'LinkedIn / X / TikTok scraping (non-x402 alternative to Apify; ~$0.15/record)',
    keyPrefix: '',
    keyPlaceholder: '00000000-0000-0000-0000-000000000000',
  },
];
